import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Video, AlertCircle } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useAnalysisContext } from '../contexts/AnalysisContext';
import { buildAnalyzingPath, persistActiveAnalysisId } from '../utils/activeAnalysisId';
import { validateTwitchUrl, INVALID_TWITCH_URL_MESSAGE } from '../utils/twitchUrlValidation';
import { apiFetch } from '../utils/apiClient';
import SignInWithTwitchButton from './SignInWithTwitchButton';
import { BrandLogo } from './BrandLogo';
import { buildGameSelectOptions, isCfbUploadOptionEnabled } from '../utils/cfbFeatureFlags';

const OAUTH_AUTH_ERRORS = new Set([
  'twitch_token_exchange_failed',
  'twitch_profile_fetch_failed',
]);

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading, isAuthenticated, authError, clearAuthError } = useUser();
  const { addAnalysis } = useAnalysisContext();
  const [url, setUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [oauthErrorBanner, setOauthErrorBanner] = useState<string | null>(null);

  const [game, setGame] = useState('');
  const [gamertag, setGamertag] = useState('');
  const [team, setTeam] = useState('');
  const [userTeam, setUserTeam] = useState('');
  const [scoutingPerspective, setScoutingPerspective] = useState('scout_opponent');

  const cfbUploadEnabled = isCfbUploadOptionEnabled();
  const gameSelectOptions = buildGameSelectOptions(cfbUploadEnabled);

  const isQuestionnaireComplete = game && gamertag.trim() && team.trim() && scoutingPerspective;

  const teamFieldLabel =
    scoutingPerspective === 'scout_my_team'
      ? 'Which team is your team?'
      : scoutingPerspective === 'scout_opponent'
        ? 'Which team do you want to scout?'
        : 'Which team should Scoutlytics focus on, if known?';

  useEffect(() => {
    if (!cfbUploadEnabled && game === 'College Football') {
      setGame('');
    }
  }, [cfbUploadEnabled, game]);

  useEffect(() => {
    const authErrorParam = searchParams.get('auth_error');
    if (authErrorParam && OAUTH_AUTH_ERRORS.has(authErrorParam)) {
      setOauthErrorBanner('Twitch sign-in failed. Please try again.');
      const next = new URLSearchParams(searchParams);
      next.delete('auth_error');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleTwitchUrlUpload = async (twitchUrl: string) => {
    if (isUploading) return;

    const trimmedUrl = twitchUrl.trim();
    if (!validateTwitchUrl(trimmedUrl)) {
      setError(INVALID_TWITCH_URL_MESSAGE);
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const isLongVideo = trimmedUrl.includes('twitch.tv/videos/');

      const formData = new FormData();
      formData.append('twitch_url', trimmedUrl);
      formData.append('game', game);
      formData.append('gamertag', gamertag);
      formData.append('team', team);
      formData.append('user_team', userTeam);
      formData.append('scouting_perspective', scoutingPerspective);

      const response = await apiFetch('/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.status === 401 || data.error === 'authentication_required') {
        setError(data.message || 'Please sign in with Twitch to analyze a VOD.');
        setIsUploading(false);
        return;
      }

      if (data.success && data.upload_id) {
        persistActiveAnalysisId(data.upload_id);
        addAnalysis(data.upload_id);
        navigate(buildAnalyzingPath(data.upload_id), {
          state: {
            analysisId: data.upload_id,
            backgroundMode: true,
            isLongVideo: isLongVideo,
            estimatedTime: '2–5 minutes',
          },
        });
        return;
      }
      setError(data.message || data.error || 'URL processing failed. Please try again.');
    } catch (uploadError) {
      setError('URL processing failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
        <p className="mt-4 text-gray-600 dark:text-gray-300">Checking sign-in status...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg p-8 text-center shadow-lg">
          <BrandLogo variant="brand" className="mx-auto justify-center mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Start a scouting report
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
            Sign in with Twitch to analyze any public Twitch VOD or clip.
          </p>
          {(oauthErrorBanner || authError) && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 dark:text-red-400">
                {oauthErrorBanner || authError}
              </span>
            </div>
          )}
          <SignInWithTwitchButton returnTo="/upload" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Upload Your Gameplay</h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          Paste a Twitch URL to analyze your gameplay
        </p>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Closed beta supports Twitch VODs and clips. Direct file upload is coming soon.
        </p>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Twitch VODs must be 120 minutes or shorter during closed beta.
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          For best results, use a 30–90 minute gameplay VOD with clear scoreboard/HUD visibility and
          multiple drives.
        </p>
      </div>

      {authError && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between">
          <span className="text-amber-800 dark:text-amber-300 text-sm">{authError}</span>
          <button
            type="button"
            onClick={clearAuthError}
            className="text-amber-700 dark:text-amber-400 text-sm underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-purple-700 dark:text-purple-400">
          Scouting Questionnaire <span className="text-red-500">*</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
              Which Game are you Scouting for? <span className="text-red-500">*</span>
            </label>
            <select
              value={game}
              onChange={(e) => setGame(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            >
              {gameSelectOptions.map((option) => (
                <option key={option.value || 'placeholder'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
              Who do you want Scoutlytics to scout? <span className="text-red-500">*</span>
            </label>
            <select
              value={scoutingPerspective}
              onChange={(e) => setScoutingPerspective(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="scout_opponent">My opponent</option>
              <option value="scout_my_team">My team</option>
              <option value="analyze_both">Analyze both teams</option>
              <option value="not_sure">Not sure</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
              What is the gamertag/PSN of the user you are scouting?{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={gamertag}
              onChange={(e) => setGamertag(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
              placeholder="Enter gamertag or PSN"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
              {teamFieldLabel} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
              placeholder="Enter team name"
            />
          </div>
          {scoutingPerspective === 'scout_opponent' && (
            <div className="md:col-span-2">
              <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                Your team (optional)
              </label>
              <input
                type="text"
                value={userTeam}
                onChange={(e) => setUserTeam(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your team name"
              />
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white dark:bg-secondary-800 border border-gray-300 dark:border-gray-600 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Video className="h-6 w-6 text-purple-600 dark:text-purple-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Paste Twitch URL</h3>
          </div>
          <div className="space-y-4">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste your Twitch VOD, clip, or channel URL"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={!isQuestionnaireComplete || isUploading}
            />

            {url.includes('twitch.tv/videos/') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      Twitch VOD Detected
                    </h3>
                    <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                      Full-length VODs are analyzed with Gemini Native Vision after secure upload.
                      Most reports finish in 2–5 minutes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => handleTwitchUrlUpload(url)}
              disabled={!url || isUploading || !isQuestionnaireComplete}
              className="w-full bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading
                ? url.includes('twitch.tv/videos/')
                  ? 'Starting VOD Analysis...'
                  : 'Analyzing Twitch Video...'
                : url.includes('twitch.tv/videos/')
                  ? 'Analyze Twitch VOD (2–5 min)'
                  : 'Analyze Twitch Video (2–5 min)'}
            </button>
          </div>
          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            <p>Supported formats:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>
                Twitch VODs:{' '}
                <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">
                  https://www.twitch.tv/videos/2493990879
                </code>
              </li>
              <li>
                Twitch Clips:{' '}
                <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">
                  https://www.twitch.tv/channel/clip/YourClipSlug
                </code>
              </li>
              <li>
                Live Streams:{' '}
                <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">
                  https://www.twitch.tv/channel
                </code>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700 dark:text-red-400">{error}</span>
        </div>
      )}

      {isUploading && (
        <div className="mt-6 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
            <span className="text-gray-700 dark:text-gray-300">Processing URL...</span>
          </div>
        </div>
      )}

      <div className="mt-6 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Processing Mode</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          All analyses run in the background. You&apos;ll get a notification via the bell when complete.
        </p>
      </div>

      <div className="mt-8 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Supported Formats</h3>
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Twitch URLs</h4>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• Twitch VODs and clips</li>
            <li>• Public Twitch content only</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Upload;
